import React, { useState, useEffect } from 'react';
import { BRAND_NAME } from '../../../theme/brand';
import {
    Box,
    Button,
    Card,
    CardBody,
    Text,

    VStack,
    HStack,
    List,
    ListItem,
    Icon,
    Code,
    Tag,
    Divider,
    Flex
} from '@chakra-ui/react';
import { CheckIcon, CopyIcon, InfoIcon, CheckCircleIcon } from '@chakra-ui/icons';
import Prism from 'prismjs';
import 'prismjs/components/prism-swift';
import 'prismjs/themes/prism-tomorrow.css';
import { useNavigate } from 'react-router-dom';

const SwiftSDKIntegration = ({ data }) => {

    const [copiedSection, setCopiedSection] = useState('');
    const navigate = useNavigate()
    const swiftAppDelegateCode = `import UIKit
import UserNotifications
import NotificationSDK

private enum AppSocketGate {
    static var manualDisconnect = false
}

class AppDelegate: NSObject, UIApplicationDelegate {
    func application(
        _ application: UIApplication,
        didFinishLaunchingWithOptions launchOptions: [UIApplication.LaunchOptionsKey: Any]?
    ) -> Bool {
        StorageManager.shared.set("pending_show_dialog", value: "false")
        StorageManager.shared.remove("pending_dialog_message")
        StorageManager.shared.remove("pending_dialog_tab")
        
        UNUserNotificationCenter.current().delegate = APNsService.shared
        APNsService.shared.configure()
        
        NotificationCenter.default.addObserver(
            self,
            selector: #selector(handleDidEnterBackground),
            name: UIApplication.didEnterBackgroundNotification,
            object: nil
        )
        NotificationCenter.default.addObserver(
            self,
            selector: #selector(handleWillEnterForeground),
            name: UIApplication.willEnterForegroundNotification,
            object: nil
        )
        
        if let userInfo = launchOptions?[.remoteNotification] as? [AnyHashable: Any] {
            APNsService.shared.handlePushPayload(userInfo: userInfo, launchedFromTap: true)
        }
        
        return true
    }
    
    func application(
        _ application: UIApplication,
        didRegisterForRemoteNotificationsWithDeviceToken deviceToken: Data
    ) {
        let tokenString = deviceToken.map { String(format: "%02.2hhx", $0) }.joined()
        APNsService.shared.deviceToken = tokenString
        StorageManager.shared.set("apns_token", value: tokenString)
    }
    
    func application(
        _ application: UIApplication,
        didFailToRegisterForRemoteNotificationsWithError error: Error
    ) {
        print("APNs registration failed: \\(error.localizedDescription)")
    }
    
    func application(
        _ application: UIApplication,
        didReceiveRemoteNotification userInfo: [AnyHashable: Any],
        fetchCompletionHandler completionHandler: @escaping (UIBackgroundFetchResult) -> Void
    ) {
        APNsService.shared.handlePushPayload(userInfo: userInfo, launchedFromTap: false)
        UserDefaults.standard.set(Date(), forKey: "lastSilentPushReceived")
        completionHandler(.newData)
    }
    
    func applicationDidBecomeActive(_ application: UIApplication) {
        if #available(iOS 17.0, *) {
            UNUserNotificationCenter.current().setBadgeCount(0, withCompletionHandler: nil)
        } else {
            application.applicationIconBadgeNumber = 0
        }
        
        DispatchQueue.main.asyncAfter(deadline: .now() + 1.0) {
            if UIApplication.shared.isRegisteredForRemoteNotifications == false {
                UIApplication.shared.registerForRemoteNotifications()
            }
        }
        
        guard AppSocketGate.manualDisconnect == true else { return }
        guard
            let userId = StorageManager.shared.get("current_user_id"),
            let propertyId = StorageManager.shared.get("property_id")
        else { return }
        
        AppSocketGate.manualDisconnect = false
        DispatchQueue.main.asyncAfter(deadline: .now() + 0.4) {
            SocketService.shared.start(
                userId: userId,
                propertyId: propertyId,
                apnsToken: APNsService.shared.deviceToken
            )
        }
    }
    
    func applicationDidEnterBackground(_ application: UIApplication) {
        AppSocketGate.manualDisconnect = true
        WebSocketManager.shared.disconnect()
    }
    
    func applicationWillResignActive(_ application: UIApplication) {}
    
    func applicationWillTerminate(_ application: UIApplication) {
        WebSocketManager.shared.disconnect()
    }
    
    @objc private func handleDidEnterBackground() {
        applicationDidEnterBackground(UIApplication.shared)
    }
    
    @objc private func handleWillEnterForeground() {}
}`;

    const copyToClipboard = (text, section) => {
        navigator.clipboard.writeText(text);
        setCopiedSection(section);
        setTimeout(() => setCopiedSection(''), 2000);
    };

    const handleSwiftConfigure = () => {
        const fallbackConfig = {
            team_id: "",
            key_id: "",
            bundle_id: ""
        };

        const iosConfig = data?.ios_config;

        const iosConfiguration =
            iosConfig &&
                typeof iosConfig === "object" &&
                !Array.isArray(iosConfig) &&
                Object.keys(iosConfig).length > 0
                ? iosConfig
                : fallbackConfig;

        navigate("/documentation/configure-apn", {
            state: { iosConfiguration }
        });
    };


    const isSwiftConfigured = data?.ios;

    const sdkConfigCode = `// In AppDelegate – didFinishLaunchingWithOptions
UNUserNotificationCenter.current().delegate = APNsService.shared
APNsService.shared.configure()`;

    const notifPermissionCode = `UNUserNotificationCenter.current().requestAuthorization(
    options: [.alert, .badge, .sound]
) { granted, error in
    if granted {
        DispatchQueue.main.async {
            UIApplication.shared.registerForRemoteNotifications()
        }
    }
}`;

    const userLoginCode = `// After successful user login
SocketService.shared.start(
    userId: "USER_PHONE_NUMBER",
    propertyId: "YOUR_PROPERTY_ID",
    apnsToken: APNsService.shared.deviceToken
)`;

    const openNotifCenterCode = `APNsService.shared.openNotifications()`;

    const getUnreadCountCode = `APNsService.shared.getUnreadCount { count in
    print("Unread notifications: \\(count)")
}`;

    const logoutCode = `// On user logout
WebSocketManager.shared.disconnect()
// Clear your application's session data`;

    const CodeBlock = ({ code, section, language = 'swift' }) => {
        useEffect(() => {
            Prism.highlightAll();
        }, [code]);

        return (
            <Card bg="black" color="white" position="relative" p={4}>
                <CardBody p={0}>
                    <Button
                        position="absolute"
                        top={3}
                        right={3}
                        size="sm"
                        variant="ghost"
                        colorScheme="whiteAlpha"
                        onClick={() => copyToClipboard(code, section)}
                        zIndex={10}
                    >
                        {copiedSection === section ? <CheckIcon w={4} h={4} /> : <CopyIcon w={4} h={4} />}
                    </Button>
                    <Box as="pre" className="language-${language}" style={{ backgroundColor: 'black', textShadow: 'none', color: 'white', padding: '0px', borderRadius: '8px' }}>
                        <code className={`language-${language}`}>{code}</code>
                    </Box>
                </CardBody>
            </Card>
        );
    };

    const ConfigBanner = () => (
        <Box
            borderRadius="8.75px"
            border="1px solid"
            borderColor={isSwiftConfigured ? "#00c951" : "#ff8904"}
            bg={isSwiftConfigured ? "#f0fdf4" : "#fff7ed"}
            p={4}
            mb={4}
        >
            <HStack spacing={3} align="center">
                <Icon
                    as={isSwiftConfigured ? CheckCircleIcon : InfoIcon}
                    color={isSwiftConfigured ? "green.500" : "orange.500"}
                    boxSize={5}
                />
                <Box flex="1">
                    <HStack justify="space-between" align="center">
                        <Box>
                            <Text fontSize="md" fontWeight="semibold" color={isSwiftConfigured ? "green.800" : "orange.800"} mb={1}>
                                Push Notification (APN) {isSwiftConfigured ? "Configured" : "Not Configured"}
                            </Text>
                            <Text fontSize="sm" color={isSwiftConfigured ? "green.700" : "orange.700"}>
                                {isSwiftConfigured
                                    ? "Your push notification service is successfully configured and ready to use."
                                    : "Configure your push notification service to enable real-time notifications."}
                            </Text>
                        </Box>
                        <HStack spacing={3}>
                            <Button
                                bg={isSwiftConfigured ? "#00a63e" : "var(--brand-primary)"}
                                variant="solid"
                                size="sm"
                                color="white"
                                fontWeight="semibold"
                                onClick={handleSwiftConfigure}
                                _hover={{ opacity: 0.9 }}
                            >
                                {isSwiftConfigured ? "Re-Configure" : "Configure"}
                            </Button>
                        </HStack>
                    </HStack>
                </Box>
            </HStack>
        </Box>
    );

    return (
        <Box minH="100vh" >
            <Box >
                <VStack spacing={6} align="stretch">



                    <Flex justifyContent={"space-between"} alignItems={"center"}>
                        <Text fontSize={"2xl"} fontWeight={"semibold"}>iOS SDK Integration</Text>
                        <Tag bg={"#ff6b35"} color={"white"} fontSize={"xs"}>
                            Swift
                        </Tag>
                    </Flex>
                    <Divider />


                    <Text color="gray.700" fontSize="md">
                        This guide explains how to integrate the provided .xcframework into a native Swift iOS app using Xcode.
                    </Text>

                    <Text color="gray.700" fontSize={"sm"}>
                        <Text as="span" fontWeight="semibold">Swift Version:</Text> 5.0 or higher
                    </Text>


                    <ConfigBanner />

                    {/* Step 1: Unzip the Framework */}
                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>

                        <Text fontSize="xl" fontWeight={"semibold"} mb={3}>1. Unzip the Framework</Text>
                        <List spacing={3}>
                            <ListItem display="flex" alignItems="flex-start" gap={2}>
                                <Text color="orange.500" mt={1}>•</Text>
                                <Text color="gray.700">
                                    Place the ZIP file <Code>YourSDK.xcframework.zip</Code> on your Desktop.
                                </Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="flex-start" gap={2}>
                                <Text color="orange.500" mt={1}>•</Text>
                                <Text color="gray.700">
                                    Right-click the ZIP file and select <Text as="span" fontWeight="semibold">Open</Text> to extract it.
                                </Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="flex-start" gap={2}>
                                <Text color="orange.500" mt={1}>•</Text>
                                <Text color="gray.700">
                                    You will now have a folder on Desktop named: <Code>YourSDK.xcframework</Code>
                                </Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="flex-start" gap={2}>
                                <Text color="orange.500" mt={1}>•</Text>
                                <Text color="gray.700">
                                    This folder contains the SDK and must be used exactly as it is.
                                </Text>
                            </ListItem>
                        </List>

                    </Card>

                    {/* Step 2: Add the Framework to the Xcode Project */}
                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>

                        <Text fontSize="xl" fontWeight={"semibold"} mb={3}>2. Add the Framework to the Xcode Project</Text>
                        <List spacing={3}>
                            <ListItem display="flex" alignItems="flex-start" gap={2}>
                                <Text color="orange.500" mt={1}>•</Text>
                                <Text color="gray.700">Open your Xcode project.</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="flex-start" gap={2}>
                                <Text color="orange.500" mt={1}>•</Text>
                                <Text color="gray.700">
                                    From your Desktop, drag the folder <Code>YourSDK.xcframework</Code> into the Project Navigator (the left-hand panel in Xcode).
                                </Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="flex-start" gap={2}>
                                <Text color="orange.500" mt={1}>•</Text>
                                <Box>
                                    <Text color="gray.700" mb={2}>When the dialog appears, select the following options:</Text>
                                    <List spacing={1} ml={4}>
                                        <ListItem display="flex" alignItems="flex-start" gap={2}>
                                            <Text color="orange.500">◦</Text>
                                            <Text color="gray.700">
                                                <Text as="span" fontWeight="semibold">Copy items if needed</Text> → must be selected
                                            </Text>
                                        </ListItem>
                                        <ListItem display="flex" alignItems="flex-start" gap={2}>
                                            <Text color="orange.500">◦</Text>
                                            <Text color="gray.700">
                                                <Text as="span" fontWeight="semibold">Add to target</Text> → check your main app target
                                            </Text>
                                        </ListItem>
                                    </List>
                                </Box>
                            </ListItem>
                            <ListItem display="flex" alignItems="flex-start" gap={2}>
                                <Text color="orange.500" mt={1}>•</Text>
                                <Text color="gray.700">
                                    Click <Text as="span" fontWeight="semibold">Finish</Text>.
                                </Text>
                            </ListItem>
                        </List>

                    </Card>

                    {/* Step 3: Enable "Embed & Sign" */}
                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>

                        <VStack spacing={4} align="stretch">
                            <Text fontSize="xl" fontWeight="semibold">3. Enable "Embed & Sign"</Text>
                            <List spacing={3}>
                                <ListItem display="flex" alignItems="flex-start" gap={2}>
                                    <Text color="orange.500" mt={1}>•</Text>
                                    <Text color="gray.700">Select your app target in Xcode.</Text>
                                </ListItem>
                                <ListItem display="flex" alignItems="flex-start" gap={2}>
                                    <Text color="orange.500" mt={1}>•</Text>
                                    <Text color="gray.700">
                                        Open the <Text as="span" fontWeight="semibold">General</Text> tab.
                                    </Text>
                                </ListItem>
                                <ListItem display="flex" alignItems="flex-start" gap={2}>
                                    <Text color="orange.500" mt={1}>•</Text>
                                    <Text color="gray.700">
                                        Scroll down to <Text as="span" fontWeight="semibold">Frameworks, Libraries & Embedded Content</Text>.
                                    </Text>
                                </ListItem>
                                <ListItem display="flex" alignItems="flex-start" gap={2}>
                                    <Text color="orange.500" mt={1}>•</Text>
                                    <Text color="gray.700">
                                        Confirm that <Code>YourSDK.xcframework</Code> is listed.
                                    </Text>
                                </ListItem>
                                <ListItem display="flex" alignItems="flex-start" gap={2}>
                                    <Text color="orange.500" mt={1}>•</Text>
                                    <Text color="gray.700">
                                        In the "Embed" column, select: <Text as="span" fontWeight="semibold">Embed & Sign</Text>
                                    </Text>
                                </ListItem>
                            </List>

                            <Box p={4} bg="yellow.50" border="1px solid" borderColor="yellow.200" borderRadius="lg">
                                <Text color="gray.700" mb={2}>If the framework is not automatically listed, add it manually:</Text>
                                <List spacing={2}>
                                    <ListItem display="flex" alignItems="flex-start" gap={2}>
                                        <Text color="orange.500">•</Text>
                                        <Text color="gray.700">
                                            Press the <Text as="span" fontWeight="bold">+</Text> button
                                        </Text>
                                    </ListItem>
                                    <ListItem display="flex" alignItems="flex-start" gap={2}>
                                        <Text color="orange.500">•</Text>
                                        <Text color="gray.700">
                                            Choose <Code>YourSDK.xcframework</Code> from the list
                                        </Text>
                                    </ListItem>
                                    <ListItem display="flex" alignItems="flex-start" gap={2}>
                                        <Text color="orange.500">•</Text>
                                        <Text color="gray.700">
                                            Set the embed option to <Text as="span" fontWeight="semibold">Embed & Sign</Text>
                                        </Text>
                                    </ListItem>
                                </List>
                            </Box>
                        </VStack>

                    </Card>

                    {/* Step 4: Clean and Run */}
                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>

                        <Text fontSize="xl" fontWeight={"semibold"} mb={3}>4. Clean and Run</Text>
                        <List spacing={3}>
                            <ListItem display="flex" alignItems="flex-start" gap={2}>
                                <Text color="orange.500" mt={1}>•</Text>
                                <Text color="gray.700">
                                    From the top menu, select <Text as="span" fontWeight="semibold">Product → Clean Build Folder</Text>.
                                </Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="flex-start" gap={2}>
                                <Text color="orange.500" mt={1}>•</Text>
                                <Text color="gray.700">Build and run the project.</Text>
                            </ListItem>
                        </List>

                    </Card>

                    {/* Step 5: Add the Required AppDelegate Code */}
                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>

                        <VStack spacing={4} align="stretch">
                            <Text fontSize="xl" fontWeight="semibold">5. Add the Required AppDelegate Code</Text>
                            <Text color="gray.700">
                                The following AppDelegate implementation must be added to your project so the SDK can handle notifications, APNs registration, and WebSocket reconnection:
                            </Text>
                            <CodeBlock code={swiftAppDelegateCode} section="swift-appdelegate" language="swift" />
                        </VStack>

                    </Card>

                    {/* Step 6: SDK Configuration */}
                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                        <VStack spacing={4} align="stretch">
                            <Text fontSize="xl" fontWeight="semibold">6. SDK Configuration</Text>
                            <Text color="gray.700">
                                Configure the SDK in your <Text as="span" fontWeight="semibold">AppDelegate</Text> inside{' '}
                                <Code>application(_:didFinishLaunchingWithOptions:)</Code>:
                            </Text>
                            <CodeBlock code={sdkConfigCode} section="swift-sdk-config" language="swift" />
                            <Box p={3} bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md">
                                <Text fontSize="sm" fontWeight="semibold" color="blue.800" mb={2}>Required Credentials</Text>
                                <List spacing={1}>
                                    {[
                                        { label: 'propertyId', desc: `XPush Property ID provided by ${BRAND_NAME}` },
                                        { label: 'eventKey', desc: `Event Tracker Key provided by ${BRAND_NAME}` },
                                    ].map(({ label, desc }) => (
                                        <ListItem key={label} display="flex" gap={2} alignItems="flex-start">
                                            <Code fontSize="xs" flexShrink={0}>{label}</Code>
                                            <Text fontSize="sm" color="gray.700">{desc}</Text>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </VStack>
                    </Card>

                    {/* Step 7: Request Notification Permission */}
                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                        <VStack spacing={4} align="stretch">
                            <Text fontSize="xl" fontWeight="semibold">7. Request Notification Permission</Text>
                            <Text color="gray.700">
                                Request push notification permission from the user — typically on first launch or after login:
                            </Text>
                            <CodeBlock code={notifPermissionCode} section="swift-notif-perm" language="swift" />
                        </VStack>
                    </Card>

                    {/* Step 8: User Login */}
                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                        <VStack spacing={4} align="stretch">
                            <Text fontSize="xl" fontWeight="semibold">8. User Login</Text>
                            <Text color="gray.700">
                                After a successful user login, start the WebSocket connection to link push notifications and events to the user:
                            </Text>
                            <CodeBlock code={userLoginCode} section="swift-user-login" language="swift" />
                            <Text fontSize="sm" color="gray.600">
                                The APNs token is collected automatically via{' '}
                                <Code>didRegisterForRemoteNotificationsWithDeviceToken</Code> in the AppDelegate.
                            </Text>
                        </VStack>
                    </Card>

                    {/* Step 9: Notification Center */}
                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                        <VStack spacing={4} align="stretch">
                            <Text fontSize="xl" fontWeight="semibold">9. Notification Center</Text>
                            <Box>
                                <Text fontWeight="semibold" mb={2}>Open Notification Center</Text>
                                <CodeBlock code={openNotifCenterCode} section="swift-open-notif" language="swift" />
                            </Box>
                            <Box>
                                <Text fontWeight="semibold" mb={2}>Get Unread Count</Text>
                                <CodeBlock code={getUnreadCountCode} section="swift-unread-count" language="swift" />
                            </Box>
                        </VStack>
                    </Card>

                    {/* Step 10: Logout */}
                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                        <VStack spacing={4} align="stretch">
                            <Text fontSize="xl" fontWeight="semibold">10. Logout</Text>
                            <Text color="gray.700">
                                On user logout, disconnect the WebSocket to stop receiving notifications for that session:
                            </Text>
                            <CodeBlock code={logoutCode} section="swift-logout" language="swift" />
                        </VStack>
                    </Card>

                </VStack>
            </Box>
        </Box>
    );
};

export default SwiftSDKIntegration;